import { CheckCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { waitForTransactionReceipt } from '@wagmi/core';
import { message, Modal, ModalProps, notification, Spin } from 'antd';
import BigNumber from 'bignumber.js';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import secureLocalStorage from 'react-secure-storage';
import { delay } from 'src/helpers';
import { decrypt, safeJSONParse } from 'src/helpers/encryption';
import { useVaultAddress } from 'src/hooks/useVaultAddress';
import { useAppTranslation } from 'src/i18n/hooks';
import { handleTransactionError } from 'src/libs/wagmi';
import { useSetCombinePublicKey } from 'src/services/auth/set-combined-public-key';
import { buildApproveVariables, useApprove } from 'src/services/blockchain/approve';
import {
  buildDepositVariables,
  useDeposit,
  UseDepositOptions,
} from 'src/services/blockchain/deposit';
import { useMultipleErc20Balances } from 'src/services/blockchain/get-multiple-erc20-balance';
import { useSupportedTokens } from 'src/services/token/get-supported-tokens';
import { useAppSelector } from 'src/store';
import { erc20Abi, parseUnits } from 'viem';
import { useAccount, useChainId, useReadContract } from 'wagmi';
import { z } from 'zod';
import { ThemesMode } from '../..';
import SelectTokenButton from './SelectTokenButton';
import TokenInput from './TokenInput';
import './styles.scss';
import NetworkSelect from './NetworkSelect';
import Web3ActionButton from 'src/components/02.buttons/Web3ActionButton';
import { depositChainList, wagmiAdapter } from 'src/libs/web3-modal';
import { useAuth } from 'src/libs/rainbow';

interface FormData {
  token: {
    id: string;
    address?: string;
  };
  amount: string;
  selectedChain?: number;
}

enum ModalStep {
  Approve = 'Approve',
  Deposit = 'Deposit',
}

type DepositModalProps = Pick<ModalProps, 'open' | 'onClose'> & {
  onDepositSuccess?: NonNullable<UseDepositOptions<unknown>['mutation']>['onSuccess'];
  onDepositError?: NonNullable<UseDepositOptions<unknown>['mutation']>['onError'];
  onDepositSettle?: () => void;
};

const DepositModalV2 = ({ onClose, open, onDepositSettle }: DepositModalProps) => {
  const supportedTokensQuery = useSupportedTokens(1, false);
  const { address: userAddress } = useAccount();
  const approveMutation = useApprove();
  const depositMutation = useDeposit();
  const theme = useAppSelector((state) => state.theme);

  const { t } = useAppTranslation('trade');
  const [isSelectToken, setIsSelectToken] = useState(false);
  const chainId = useChainId();
  const balances = useMultipleErc20Balances({
    tokenAddresses: supportedTokensQuery.data?.map((item) => item.address) ?? [],
  });
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<ModalStep>(ModalStep.Approve);
  const vaultAddress = useVaultAddress();
  const setCombinePublicKeyMutation = useSetCombinePublicKey();
  const { isEthereumWallet } = useAuth()

  const depositFormSchema = z.object({
    token: z.object({
      id: z.string(),
      address: z.string(),
    }),
    amount: z.string().refine(
      (val) => {
        const num = Number(val);
        return !isNaN(num) && num > 0;
      },
      { message: t('depositModal.amountValidation') },
    ),
  });

  const {
    reset,
    control,
    formState: { errors, isValid },
    setValue,
    watch,
    handleSubmit,
  } = useForm<FormData>({
    resolver: zodResolver(depositFormSchema),
    defaultValues: {
      token: undefined,
      amount: '',
      selectedChain: chainId,
    },
    mode: 'all',
  });
  const selectedToken = watch('token');
  const selectedChain = watch('selectedChain');

  const { refetch } = useReadContract({
    abi: erc20Abi,
    address: selectedToken?.address as any,
    functionName: 'allowance',
    args: [userAddress!, vaultAddress],
    query: {
      enabled: false,
    },
  });

  const handleMaxClick = () => {
    if (!supportedTokensQuery.data || !balances.data) {
      message.error('Fetch Balance Error');
      return;
    }
    const foundTokenIndex = supportedTokensQuery.data?.findIndex((item) => {
      return item.token_id === selectedToken?.id;
    });

    const foundUserBalance = balances.data[foundTokenIndex];
    const max = BigNumber(foundUserBalance.result?.toString() ?? 0)
      .div(`1e${supportedTokensQuery.data[foundTokenIndex].decimal ?? 18}`)
      .toString();

    setValue('amount', max, { shouldValidate: true, shouldDirty: true });
  };

  const onApprove = async (data: FormData) => {
    if (loading) {
      return;
    }
    if (!supportedTokensQuery.data || !balances.data) {
      message.error('Fetch Balance Error');
      return;
    }
    const foundTokenIndex = supportedTokensQuery.data?.findIndex((item) => {
      return item.token_id === selectedToken?.id;
    });
    setLoading(true);

    try {
      const foundUserBalance = balances.data[foundTokenIndex];
      const maxValue = BigNumber(foundUserBalance.result?.toString() ?? 0)
        .div(`1e${supportedTokensQuery.data[foundTokenIndex].decimal ?? 18}`)
        .toString();
      if (BigNumber(data.amount).isGreaterThan(maxValue)) {
        message.error(t('depositModal.exceededBalance'));
        setLoading(false);
        return;
      }

      const userAllowance = await refetch();
      if (
        BigNumber(userAllowance.data?.toString() ?? 0)
          .div(`1e${supportedTokensQuery.data[foundTokenIndex].decimal ?? 18}`)
          .isGreaterThanOrEqualTo(data.amount)
      ) {
        setCurrentStep(ModalStep.Deposit);
      } else {
        const params = buildApproveVariables({
          amount: parseUnits(data.amount, supportedTokensQuery.data[foundTokenIndex].decimal ?? 18),
          tokenAddress: data.token?.address as `0x${string}`,
          vaultAddress,
        });
        const hash = await approveMutation.writeContractAsync(params, {
          onSuccess: async (data) => {
            console.log('txHash', data);
          },
          onError: handleTransactionError((error) => {
            try {
              console.log('🚀 ~ handleApprove ~ error:', error);
              const errorMessage = typeof error?.message === 'string' ? error.message : '';
              const detailsMatch = errorMessage.match(/Details:([\s\S]*?)Version:/);
              const detailedMessage = detailsMatch
                ? detailsMatch[1]?.trim()
                : 'An unexpected error occurred';

              if (errorMessage.includes('insufficient funds')) {
                message.error(t('depositModal.notEnoughFunds'));
              } else {
                message.error(`${t('depositModal.approveFailed')}: ${detailedMessage}`);
              }
            } catch (err) {
              console.error('Error handling transaction error:', err);
            }
          }),
        });
        await waitForTransactionReceipt(wagmiAdapter.wagmiConfig as any, {
          hash: hash,
          confirmations: 1,
        });
        setCurrentStep(ModalStep.Deposit);
      }
    } catch (error) {
      console.error(error);
    }
    setLoading(false);
  };

  const onDeposit = async (data: FormData) => {
    if (loading) {
      return;
    }
    if (!supportedTokensQuery.data || !balances.data) {
      message.error('Fetch Balance Error');
      return;
    }
    const foundTokenIndex = supportedTokensQuery.data?.findIndex((item) => {
      return item.token_id === selectedToken?.id;
    });
    setLoading(true);
    try {
      const hash = await depositMutation.writeContractAsync(
        buildDepositVariables({
          amount: parseUnits(
            data.amount!.toString(),
            supportedTokensQuery.data[foundTokenIndex].decimal ?? 18,
          ),
          tokenAddress: data.token?.address as `0x${string}`,
          vaultAddress,
        }),
        {
          onSuccess: () => {},
          onError: handleTransactionError((error) => {
            try {
              console.log('🚀 ~ handleApprove ~ error:', error);
              const errorMessage = typeof error?.message === 'string' ? error.message : '';
              const detailsMatch = errorMessage.match(/Details:([\s\S]*?)Version:/);
              const detailedMessage = detailsMatch
                ? detailsMatch[1]?.trim()
                : 'An unexpected error occurred';

              if (errorMessage.includes('insufficient funds')) {
                message.error(t('depositModal.notEnoughFunds'));
              } else {
                message.error(`${t('depositModal.approveFailed')}: ${detailedMessage}`);
              }
            } catch (err) {
              console.error('Error handling transaction error:', err);
            }
          }),
        },
      );
      await waitForTransactionReceipt(wagmiAdapter.wagmiConfig as any, {
        hash: hash,
        confirmations: 1,
      });

      notification.success({
        message: t('depositModal.depositReceiptSuccessMessage'),
        description: t('depositModal.depositReceiptSuccessDescription'),
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });

      (async () => {
        onDepositSettle?.();

        await delay(2000);
        const storedData = secureLocalStorage.getItem(`signerData-${userAddress}`) as string;
        const decryptedData = decrypt(storedData, 'vDex', userAddress as string);

        if (decryptedData) {
          const parsedData = safeJSONParse(decryptedData);
          setCombinePublicKeyMutation.mutate({
            chainID: chainId,
            publicKey: parsedData.publicKey,
          });
        }
        await balances.refetch();
      })();
    } catch (error) {}
    setLoading(false);
  };

  useEffect(() => {
    if (supportedTokensQuery?.data) {
      reset({
        token: {
          id: supportedTokensQuery.data?.[0]?.token_id,
          address: supportedTokensQuery.data?.[0]?.address,
        },
        amount: '',
        selectedChain: selectedChain,
      });
    }
  }, [supportedTokensQuery?.data]);

  useEffect(() => {
    if (!open) {
      setCurrentStep(ModalStep.Approve);
      setLoading(false);
      if (supportedTokensQuery.data) {
        reset({
          token: {
            id: supportedTokensQuery.data?.[0]?.token_id,
            address: supportedTokensQuery.data?.[0]?.address,
          },
          amount: '',
          selectedChain: selectedChain,
        });
      } else {
        reset({
          token: undefined,
          amount: '',
          selectedChain: selectedChain,
        });
      }
    }
  }, [open]);

  useEffect(() => {
    if (!isEthereumWallet) {
      onClose?.({} as any);
    }
  }, [isEthereumWallet]);

  return (
    <Modal
      destroyOnClose={true}
      open={open}
      onCancel={onClose}
      footer={<></>}
      className={`z-10 font-poppins deposit-modal overflow-hidden ${theme}`}
    >
      <div className="">
        <div className="ant-modal-header flex items-center gap-[10px]">
          {isSelectToken && (
            <div onClick={() => setIsSelectToken(false)} className="cursor-pointer">
              <ChevronLeft size={20} />
            </div>
          )}
          <span className="text-[18px] font-bold">
            {isSelectToken ? t('depositModal.selectAsset') : t('depositModal.title')}
          </span>
        </div>
        {currentStep === ModalStep.Approve ? (
          <div className="modal-item w-full px-[10px] sm:px-[24px] pb-[10px] sm:pb-[24px] pt-[10px]">
            <div className="flex flex-col items-center gap-[20px] border-[#494950] border-[1px] px-[10px] rounded-[16px] py-[20px]">
              <div className="flex gap-[20px]">
                <div className="px-[10px]">
                  <p className="text-[16px] font-bold">
                    {t('depositModal.approveYourSpendingCap')}
                  </p>
                  <p className="text-[12px]">{t('depositModal.grantVdexPermission')}</p>
                </div>
                <div className="w-[20%] flex justify-center items-center">
                  {loading && (
                    <Spin
                      size={'large'}
                      indicator={
                        <LoadingOutlined
                          style={{
                            color: theme === ThemesMode.dark ? 'white' : '#494950',
                          }}
                          spin
                        />
                      }
                    />
                  )}
                </div>
              </div>
              <Controller
                name="selectedChain"
                control={control}
                render={({ field: { value, onChange } }) => {
                  const supportedNetworks = depositChainList.map((item) => {
                    return {
                      chain_id: item.id,
                      chain_name: item.name,
                      connected: value === item.id,
                    };
                  });
                  return <NetworkSelect onSelectChain={onChange} chainData={supportedNetworks} />;
                }}
              />
              <Controller
                name="token"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <SelectTokenButton
                      balances={balances.data ?? []}
                      supportedTokens={supportedTokensQuery?.data ?? []}
                      selectedToken={value?.id}
                      onSelectToken={(token) => {
                        onChange({
                          id: token.token_id,
                          address: token.address,
                        });
                      }}
                    />
                  );
                }}
              />
              <Controller
                name="amount"
                control={control}
                render={({ field: { value, onChange } }) => {
                  return (
                    <TokenInput
                      errorMessage={errors.amount?.message}
                      value={value}
                      onChange={onChange}
                      onClickMax={handleMaxClick}
                    />
                  );
                }}
              />
            </div>

            <Web3ActionButton selectedNetwork={selectedChain}>
              <button
                disabled={loading || !isValid}
                onClick={handleSubmit(onApprove)}
                className="font-bold bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] hover:opacity-[0.9] w-full text-white px-4 py-2 rounded-[10px] mt-4 border-[#494950] border-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('depositModal.approveFunds')}{' '}
                {loading && (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{
                          color: 'white',
                        }}
                        spin
                      />
                    }
                  />
                )}
              </button>
            </Web3ActionButton>
          </div>
        ) : (
          <div className="px-[10px] pb-[20px] sm:px-[24px] pt-[10px] flex flex-col gap-[20px]">
            <div className="flex flex-col items-center gap-[20px] border-[#494950] border-[1px] px-[10px] rounded-[16px] py-[20px]">
              <div className="flex gap-[20px]">
                <div className="px-[10px]">
                  <p className="text-[16px] font-bold">
                    {t('depositModal.approveYourSpendingCap')}
                  </p>
                  <p className="text-[12px]">{t('depositModal.grantVdexPermission')}</p>
                </div>
                <div className="w-[20%] flex justify-center items-center">
                  <CheckCircleOutlined
                    style={{
                      fontSize: '36px',
                      color: '#0ac30a',
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-item w-full">
              <div className="flex flex-col items-center gap-[20px] border-[#494950] border-[1px] px-[10px] rounded-[16px] py-[20px]">
                <div className="flex gap-[20px]">
                  <div className="px-[10px]">
                    <p className="text-[16px] font-bold">
                      {t('depositModal.confirmYourTransaction')}
                    </p>
                    <p className="text-[12px]">{t('depositModal.reviewAndConfirmTransaction')}</p>
                  </div>
                  <div className="w-[20%] flex justify-center items-center">
                    {loading && (
                      <Spin
                        size={'large'}
                        indicator={
                          <LoadingOutlined
                            style={{
                              color: theme === ThemesMode.dark ? 'white' : '#494950',
                            }}
                            spin
                          />
                        }
                      />
                    )}
                  </div>
                </div>
                <Controller
                  name="token"
                  control={control}
                  render={({ field: { value } }) => {
                    return (
                      <SelectTokenButton
                        disabled
                        supportedTokens={supportedTokensQuery?.data ?? []}
                        balances={balances?.data ?? []}
                        selectedToken={value?.id}
                        onSelectToken={() => {}}
                      />
                    );
                  }}
                />
                <Controller
                  name="amount"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <TokenInput
                        disabled
                        errorMessage={errors.amount?.message}
                        value={value}
                        onChange={onChange}
                        onClickMax={handleMaxClick}
                      />
                    );
                  }}
                />
              </div>
              <button
                disabled={loading || !isValid}
                onClick={handleSubmit(onDeposit)}
                className="font-bold bg-gradient-to-tr from-[#0f1fd0] via-[#7110e4] via-[#6f10e4] to-[#c403f6] hover:opacity-[0.9] w-full text-white px-4 py-2 rounded-[10px] mt-4 border-[#494950] border-[1px] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('depositModal.depositFund')}{' '}
                {loading && (
                  <Spin
                    indicator={
                      <LoadingOutlined
                        style={{
                          color: 'white',
                        }}
                        spin
                      />
                    }
                  />
                )}
              </button>
              <div className="text-xs mt-2">
                <p className="mb-1">{t('depositModal.note')}</p>
                <ul className="list-disc list-inside ml-4">
                  <li>{t('depositModal.noteBullet1')}</li>
                  <li>{t('depositModal.noteBullet2')}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DepositModalV2;
